import type { TaskNode } from "./types";
import { newTask } from "./model";
import { childSum } from "./tree";
import { fmt } from "./format";

const HOURS_PER_DAY = 8;
const DAYS_PER_WEEK = 5;

// pull a trailing estimate like "- 1d", "-1w", "- 4h" off a label.
// returns { label, estimate } with estimate in days (0 if none).
export function extractEstimate(text: string): { label: string; estimate: number } {
  const m = text.match(/[-–—]\s*([\d.,]+)\s*(w|wk|weeks?|d|days?|h|hrs?|hours?|m|mins?|minutes?)\.?\s*$/i);
  if (!m) return { label: text.trim(), estimate: 0 };
  const value = parseFloat(m[1].replace(",", "."));
  const unit = m[2].toLowerCase();
  let days = 0;
  if (unit.startsWith("w")) days = value * DAYS_PER_WEEK;
  else if (unit.startsWith("d")) days = value;
  else if (unit.startsWith("h")) days = value / HOURS_PER_DAY;
  else if (unit.startsWith("m")) days = value / (HOURS_PER_DAY * 60);
  const label = text.slice(0, m.index).trim().replace(/[-–—\s]+$/, "").trim();
  return { label, estimate: Math.round(days * 1000) / 1000 };
}

// parse an indented outline into a task tree
export function parseOutline(text: string): TaskNode[] {
  const roots: TaskNode[] = [];
  // stack of { indent, node }; numbered "N." items use indent -1 so any bullet nests under them
  const stack: { indent: number; node: TaskNode }[] = [];
  const lines = text.replace(/\t/g, "  ").split(/\r?\n/);

  for (const raw of lines) {
    if (!raw.trim()) continue;
    if (/^\s*total\b/i.test(raw)) continue; // skip "Total estimate: ..."

    const indentMatch = raw.match(/^(\s*)/)!;
    let indent = indentMatch[1].length;
    let rest = raw.trim();

    const numbered = rest.match(/^(\d+)\.\s+(.*)$/);
    if (numbered) {
      rest = numbered[2];
      indent = -1; // force top level
    } else {
      rest = rest.replace(/^[-*•]\s*/, ""); // strip bullet marker
    }

    const { label, estimate } = extractEstimate(rest);
    const node = newTask(label || "Untitled");
    node.estimate = estimate;
    node.children = [];

    // find parent: pop until the top of the stack is shallower than this line
    while (stack.length && stack[stack.length - 1].indent >= indent) stack.pop();

    if (stack.length === 0) roots.push(node);
    else stack[stack.length - 1].node.children.push(node);
    stack.push({ indent, node });
  }
  return roots;
}

// serialize the tree back to the indented outline format (estimates in days)
export function treeToOutline(nodes: TaskNode[], depth = 0): string[] {
  let lines: string[] = [];
  nodes.forEach((node, i) => {
    const marker = depth === 0 ? i + 1 + ". " : "- ";
    let line = "  ".repeat(depth) + marker + node.name;
    const isLeafLike = node.children.length === 0 || childSum(node) === 0;
    if (isLeafLike && node.estimate > 0) line += " - " + fmt(node.estimate) + "d";
    lines.push(line);
    if (node.children.length) lines = lines.concat(treeToOutline(node.children, depth + 1));
  });
  return lines;
}
